import { SidebarService } from '../../../services/sidebar.service';
import { SidebarContentComponent } from './sidebar-content-component';
import { SidebarChangedEvent } from '../../../utils/events/sidebar-changed-event';
import { ApplicationStateService } from '../../../services/application-state.service';
import { ModalService } from '../../../services/modal.service';
import { HomeworkComponent } from '../tasks/homework/homework.component';
import { NotesContentComponent } from '../notes-content/notes-content.component';
import { SearchComponent } from '../search/search.component';
import { ChapterContentComponent } from '../chapter-content/chapter-content.component';
import { PagesContentComponent } from '../pages-content/pages-content.component';
import { MobileBooksComponent } from '../mobile-books/mobile-books.component';
import { TasksComponent } from '../tasks/tasks.component';
import { WhiteboardDrawingContentComponent } from '../whiteboard-drawing-content/whiteboard-drawing-content.component';
import { TestGeneratorComponent } from '../tasks/test-generator/test-generator.component';
import {
    OfflineModeModalComponent
} from '../../bookviewer/pages/modal-container/offline-mode-modal/offline-mode-modal.component';
import { WorksheetsComponent } from '../worksheets/worksheets.component';
import { DigiTrainerComponent } from '../digi-trainer/digi-trainer.component';
import { BookFeatureService } from '../../../services/book-feature.service';

/**
 * Provides function to open the sidebar.
 */
export class AbstractSidebarComponent {

    NotesContentComponent = NotesContentComponent;
    SearchComponent = SearchComponent;
    HomeworkComponent = HomeworkComponent;
    TasksComponent = TasksComponent;
    TestGeneratorComponent = TestGeneratorComponent;
    ChapterContentComponent = ChapterContentComponent;
    PagesContentComponent = PagesContentComponent;
    MobileBooksComponent = MobileBooksComponent;
    WhiteboardDrawingContentComponent = WhiteboardDrawingContentComponent;
    WorksheetsComponent = WorksheetsComponent;
    DigiTrainerComponent = DigiTrainerComponent;

    protected sidebarService: SidebarService;
    protected applicationStateService: ApplicationStateService;
    protected modalService: ModalService;
    protected bookFeatureService: BookFeatureService

    openComponentInSidebar(sidebarContentComponent: SidebarContentComponent): void {

        this.handleClosingOfTools(sidebarContentComponent);

        if (!this.sidebarService || !this.applicationStateService || !this.modalService) {
            throw new Error('Services are not provided');
        }

        if (this.applicationStateService.isOfflineModeEnabled) {
            if (sidebarContentComponent === DigiTrainerComponent ||
                sidebarContentComponent === TasksComponent ||
                sidebarContentComponent === WorksheetsComponent
            ) {
                this.modalService.openModal(OfflineModeModalComponent);

                return;
            }
        }

        if (!this.checkAuthorization(sidebarContentComponent)) {
            return;
        }

        if ((this.applicationStateService.isOfflineModeEnabled &&
                sidebarContentComponent !== NotesContentComponent &&
                sidebarContentComponent !== SearchComponent &&
                sidebarContentComponent !== ChapterContentComponent &&
                sidebarContentComponent !== PagesContentComponent) ||
            !this.checkAuthorization(sidebarContentComponent)) {
            return;
        }

        const shouldOpenSidebar = sidebarContentComponent !== this.sidebarService.activeComponent;
        let event;
        if (shouldOpenSidebar) {
            event = new SidebarChangedEvent(shouldOpenSidebar, sidebarContentComponent);
        } else {
            event = new SidebarChangedEvent(shouldOpenSidebar);
        }
        this.sidebarService.triggerSidebarChangeEvent(event);
    }

    checkAuthorization(sidebarContentComponent: SidebarContentComponent): boolean {

        if (sidebarContentComponent === HomeworkComponent &&
            !this.sidebarService.wasHSMOpened() &&
            !this.applicationStateService.bookConfig.bookPermissions.homework.create) {
            this.modalService.openDisabledHSMModal();

            return false;
        }

        if (sidebarContentComponent === TasksComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.homework.create) {
            this.modalService.openDisabledHSMModal();

            return false;
        }

        if (sidebarContentComponent === NotesContentComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.rect.use) {
            this.modalService.openDisabledNotesModal();

            return false;
        }

        if (sidebarContentComponent === ChapterContentComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.bookChapter.view) {
            this.modalService.openDisabledChapterModal();

            return false;
        }

        if (sidebarContentComponent === SearchComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.pageTexts.view) {
            this.modalService.openDisabledSearchModal();

            return false;
        }

        if (sidebarContentComponent === WorksheetsComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.calcuflowLinks.view) {
            this.modalService.openDisabledCalcuflowModal();

            return false;
        }

        if (sidebarContentComponent === DigiTrainerComponent &&
            !this.applicationStateService.bookConfig.bookPermissions.exerciseSeries.view) {
            this.modalService.openDisabledDigiTrainerModal();

            return false;
        }

        return true;
    }

    /**
     * controls whether BookViewer tools are closed when sidebar component is clicked.
     */
    private handleClosingOfTools(sidebarContentComponent: SidebarContentComponent): void {
        if (this.applicationStateService.pageEditMode?.type === 'DRAW' &&
            (sidebarContentComponent !== ChapterContentComponent &&
             sidebarContentComponent !== PagesContentComponent)) {
                this.bookFeatureService.dismissMode();
            }

        if ((this.applicationStateService.pageEditMode?.type === 'RECT' ||
            this.applicationStateService.pageEditMode?.type === 'SELECTION') &&
            (sidebarContentComponent !== ChapterContentComponent &&
             sidebarContentComponent !== PagesContentComponent &&
             sidebarContentComponent !== NotesContentComponent)) {
                this.bookFeatureService.dismissMode();
            }
    }
}
